"use client";

import type { MuhuratResponse } from "@/lib/api/muhurat";
import Explainable from "@ishvaram/ui/Explainable";
import { useExplainableHandlers, useExplainer } from "@/hooks/useExplainer";

interface PanchangSummaryProps {
  data: MuhuratResponse;
}

const NAKSHATRA_HAS_EXPLAINER = new Set([
  "ashwini",
  "bharani",
  "krittika",
  "rohini",
  "mrigashira",
  "ardra",
  "punarvasu",
  "pushya",
  "ashlesha",
  "magha",
  "purva_phalguni",
  "uttara_phalguni",
  "hasta",
  "chitra",
  "swati",
  "vishakha",
  "anuradha",
  "jyeshtha",
  "moola",
  "purva_ashadha",
  "uttara_ashadha",
  "shravana",
  "dhanishta",
  "shatabhisha",
  "purva_bhadrapada",
  "uttara_bhadrapada",
  "revati",
]);

/** Convert display name → underscore ID (e.g. "Purva Phalguni" → "purva_phalguni") */
function toNakshatraId(nameEn: string): string {
  return nameEn.toLowerCase().replace(/\s+/g, "_");
}

function NakshatraNameLabel({ nameEn, nameHi }: { nameEn: string; nameHi: string }) {
  const nakshatraId = toNakshatraId(nameEn);
  const hasExplainer = NAKSHATRA_HAS_EXPLAINER.has(nakshatraId);
  const handlers = useExplainableHandlers("muhurat_panchang_summary");
  const { data, isLoading, error, refetch } = useExplainer("nakshatra", nakshatraId, "medium", {
    enabled: false,
  });
  return (
    <>
      <span className="block text-[10px] font-bold text-ish-text-muted uppercase tracking-widest mb-1.5">
        Nakshatra
      </span>
      {hasExplainer ? (
        <Explainable
          type="nakshatra"
          id={nakshatraId}
          intensity="medium"
          triggerLabel={nameEn}
          data={data}
          loading={isLoading}
          error={error ?? null}
          onRequestData={() => void refetch()}
          {...handlers}
          className="block text-base font-bold text-ish-dark"
        />
      ) : (
        <span className="block text-base font-bold text-ish-dark">{nameEn}</span>
      )}
      <div className="flex flex-wrap items-center gap-1.5 mt-0.5">
        <span className="text-xs text-ish-text-muted font-medium">{nameHi}</span>
      </div>
    </>
  );
}

export function PanchangSummary({ data }: PanchangSummaryProps) {
  return (
    <div className="bg-white rounded-2xl border border-ish-border p-6 sm:p-8 shadow-sm">
      <h2 className="font-serif text-lg font-bold text-ish-dark mb-6">Panchang at Sunrise</h2>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-6">
        {/* Tithi */}
        <div>
          <span className="block text-[10px] font-bold text-ish-text-muted uppercase tracking-widest mb-1.5">
            Tithi
          </span>
          <span className="block text-base font-bold text-ish-dark">
            {data.panchang.tithi.name_en}
          </span>
          <div className="flex flex-wrap items-center gap-1.5 mt-0.5">
            <span className="text-xs text-ish-text-muted font-medium">
              {data.panchang.tithi.name_hi}
            </span>
            <span className="text-ish-text-muted/30">&middot;</span>
            <span className="text-xs text-ish-text-muted font-medium">
              {data.panchang.tithi.paksha === "shukla" ? "Shukla Paksha" : "Krishna Paksha"}
            </span>
          </div>
        </div>

        {/* Nakshatra — uses Explainable */}
        <div>
          <NakshatraNameLabel
            nameEn={data.panchang.nakshatra.name_en}
            nameHi={data.panchang.nakshatra.name_hi}
          />
        </div>

        {/* Yoga */}
        <div>
          <span className="block text-[10px] font-bold text-ish-text-muted uppercase tracking-widest mb-1.5">
            Yoga
          </span>
          <span className="block text-base font-bold text-ish-dark">
            {data.panchang.yoga.name_en}
          </span>
          <div className="flex flex-wrap items-center gap-1.5 mt-0.5">
            <span className="text-xs text-ish-text-muted font-medium">
              {data.panchang.yoga.name_hi}
            </span>
            {data.panchang.yoga.quality && (
              <>
                <span className="text-ish-text-muted/30">&middot;</span>
                <span
                  className={`text-xs font-bold ${
                    data.panchang.yoga.quality === "shubh" ? "text-green-600" : "text-red-600"
                  }`}
                >
                  {data.panchang.yoga.quality === "shubh" ? "Shubh" : "Ashubh"}
                </span>
              </>
            )}
          </div>
        </div>

        {/* Karana */}
        <div>
          <span className="block text-[10px] font-bold text-ish-text-muted uppercase tracking-widest mb-1.5">
            Karana
          </span>
          <span className="block text-base font-bold text-ish-dark">
            {data.panchang.karana.name_en}
          </span>
          <div className="flex flex-wrap items-center gap-1.5 mt-0.5">
            <span className="text-xs text-ish-text-muted font-medium">
              {data.panchang.karana.name_hi}
            </span>
          </div>
        </div>
      </div>

      <div className="mt-6 pt-6 border-t border-ish-border/50">
        <div className="flex items-baseline gap-2">
          <span className="text-sm font-bold text-ish-dark">{data.vara.name_en}</span>
          <span className="text-xs text-ish-text-muted font-medium">({data.vara.name_hi})</span>
          <span className="text-ish-text-muted/30">&middot;</span>
          <span className="text-xs text-ish-text-muted font-medium italic">
            Ruled by{" "}
            <span className="capitalize not-italic font-bold text-ish-dark/80">
              {data.vara.ruler}
            </span>
          </span>
        </div>
      </div>
    </div>
  );
}
